import { isNumber } from "@common/helpers/typeGuards";
import {
    AmountRoundingLevel,
    RuleActionType,
    RuleConditionAmount,
    RuleConditionType,
    TransactionRuleCondition,
    TransferRuleDirection,
    serializeAccrualAccountActionValue,
    serializeAssignToScheduleActionValue,
} from "@common/types/transactionRule";
import { SplitActionFE } from "@common/helpers/rules";
import { optionalSplitDescription } from "@common/helpers/splitRuleAction";
import {
    AmountConditionType,
    AmountRoundingConditionType,
    DescriptionConditionType,
    FormRuleActionType,
    FormRuleConditionType,
    TransactionRuleActionFormSchema,
    TransactionRuleConditionFormSchema,
} from "./constants";

export function conditionFormRowToModel(
    condition: TransactionRuleConditionFormSchema,
): Omit<TransactionRuleCondition, "id"> | undefined {
    switch (condition.conditionType) {
        case FormRuleConditionType.CATEGORY:
            return buildCategoryConditionFromFormRow(condition);
        case FormRuleConditionType.SUGGESTED_CATEGORY:
            return buildSuggestedCategoryConditionFromFormRow(condition);
        case FormRuleConditionType.AMOUNT:
            return buildAmountConditionFromFormRow(condition);
        case FormRuleConditionType.BANK_DESCRIPTION:
            return buildDescriptionConditionFromFormRow(condition);
        case FormRuleConditionType.ACCOUNT:
            return buildAccountConditionFromFormRow(condition);
        case FormRuleConditionType.DATE:
            return buildDateConditionFromFormRow(condition);
        case FormRuleConditionType.ENTITY:
            return buildEntityConditionFromFormRow(condition);
        case FormRuleConditionType.COUNTERPARTY:
            return buildCounterpartyConditionFromFormRow(condition);
        case FormRuleConditionType.DATE_PROXIMITY:
            return buildDateProximityConditionFromFormRow(condition);
        case FormRuleConditionType.CLASS:
            return buildClassConditionFromFormRow(condition);
        case FormRuleConditionType.SIMILAR_TO:
            return buildSimilarToConditionFromFormRow(condition);
    }
}

function buildClassConditionFromFormRow(
    condition: TransactionRuleConditionFormSchema,
): Omit<TransactionRuleCondition, "id"> | undefined {
    if (condition.classIds?.length) {
        return {
            conditionType: RuleConditionType.CLASS,
            value: condition.classIds,
            transferDirection: condition.transferDirection,
        };
    }
}

function buildCategoryConditionFromFormRow(
    condition: TransactionRuleConditionFormSchema,
): Omit<TransactionRuleCondition, "id"> | undefined {
    if (condition.categoryIds?.length) {
        return {
            conditionType: RuleConditionType.CATEGORY,
            value: condition.categoryIds,
            transferDirection: condition.transferDirection,
        };
    }
}

function buildSuggestedCategoryConditionFromFormRow(
    condition: TransactionRuleConditionFormSchema,
): Omit<TransactionRuleCondition, "id"> | undefined {
    if (condition.categoryIds?.length) {
        return {
            conditionType: RuleConditionType.SUGGESTED_CATEGORY,
            value: condition.categoryIds,
            transferDirection: condition.transferDirection,
        };
    }
}

function buildAmountConditionFromFormRow(
    condition: TransactionRuleConditionFormSchema,
): Omit<TransactionRuleCondition, "id"> | undefined {
    if (
        condition.amountConditionType === AmountConditionType.IS_ROUND &&
        condition.amountRoundingLevel
    ) {
        return {
            conditionType: RuleConditionType.AMOUNT_IS_ROUND,
            value: {
                level: AMOUNT_ROUNDING_FORM_TO_MODEL[
                    condition.amountRoundingLevel
                ],
                isCredit: condition.amountIsCredit,
            },
            transferDirection: condition.transferDirection,
        };
    }

    if (condition.amountConditionType === AmountConditionType.IS_NOT_ROUND) {
        return {
            conditionType: RuleConditionType.AMOUNT_IS_NOT_ROUND,
            value: {
                level: AmountRoundingLevel.DOLLAR,
                isCredit: condition.amountIsCredit,
            },
            transferDirection: condition.transferDirection,
        };
    }

    if (condition.amountConditionType === AmountConditionType.IS_ROUND) {
        return undefined;
    }

    const value: RuleConditionAmount = {};

    if (
        condition.amountConditionType === AmountConditionType.BETWEEN &&
        isNumber(condition.amountValueRange?.min) &&
        isNumber(condition.amountValueRange?.max)
    ) {
        value.min = condition.amountIsCredit
            ? condition.amountValueRange.min
            : -condition.amountValueRange.max;
        value.max = condition.amountIsCredit
            ? condition.amountValueRange.max
            : -condition.amountValueRange.min;
    }

    if (
        condition.amountConditionType === AmountConditionType.GREATER_THAN &&
        isNumber(condition.amountSingleValue)
    ) {
        if (condition.amountIsCredit) {
            value.min = condition.amountSingleValue;
        } else {
            value.max = -condition.amountSingleValue;
        }
    }

    if (
        condition.amountConditionType === AmountConditionType.LESS_THAN &&
        isNumber(condition.amountSingleValue)
    ) {
        if (condition.amountIsCredit) {
            value.min = 0;
            value.max = condition.amountSingleValue;
        } else {
            value.min = -condition.amountSingleValue;
            value.max = 0;
        }
    }

    if (
        condition.amountConditionType === AmountConditionType.IS &&
        isNumber(condition.amountSingleValue)
    ) {
        if (condition.amountIsCredit) {
            value.min = condition.amountSingleValue;
            value.max = condition.amountSingleValue;
        } else {
            value.min = -condition.amountSingleValue;
            value.max = -condition.amountSingleValue;
        }
    }

    return {
        conditionType: RuleConditionType.AMOUNT,
        value,
        transferDirection: condition.transferDirection,
    };
}

const mapRuleconditionTypeToDescriptionConditionType = (
    conditionType: DescriptionConditionType,
) => {
    switch (conditionType) {
        case DescriptionConditionType.IS:
            return RuleConditionType.DESCRIPTION_IS;
        case DescriptionConditionType.CONTAINS:
            return RuleConditionType.DESCRIPTION_INCLUDES;
        case DescriptionConditionType.DOES_NOT_CONTAIN:
            return RuleConditionType.DESCRIPTION_DOES_NOT_INCLUDE;
        default:
            return RuleConditionType.DESCRIPTION_IS;
    }
};

function buildDescriptionConditionFromFormRow(
    condition: TransactionRuleConditionFormSchema,
): Omit<TransactionRuleCondition, "id"> | undefined {
    if (condition.descriptionValue) {
        return {
            conditionType: mapRuleconditionTypeToDescriptionConditionType(
                condition.descriptionConditionType,
            ),
            value: condition.descriptionValue,
            transferDirection: condition.transferDirection,
        };
    }
}

function buildAccountConditionFromFormRow(
    condition: TransactionRuleConditionFormSchema,
): Omit<TransactionRuleCondition, "id"> | undefined {
    if (condition.accountValue?.length) {
        return {
            conditionType: RuleConditionType.FINANCIAL_ACCOUNTS,
            value: condition.accountValue,
            transferDirection: condition.transferDirection,
        };
    }

    if (condition.subaccountValue?.length) {
        return {
            conditionType: RuleConditionType.SUBACCOUNTS,
            value: condition.subaccountValue,
            transferDirection: condition.transferDirection,
        };
    }
}

function buildDateConditionFromFormRow(
    condition: TransactionRuleConditionFormSchema,
): Omit<TransactionRuleCondition, "id"> | undefined {
    if (condition.dateValueRange?.after || condition.dateValueRange?.before) {
        // here we check if they are in the correct "order", and if not, we can swap them
        if (condition.dateValueRange.after && condition.dateValueRange.before) {
            if (
                new Date(condition.dateValueRange.after) >
                new Date(condition.dateValueRange.before)
            ) {
                const temp = condition.dateValueRange.after;
                condition.dateValueRange.after =
                    condition.dateValueRange.before;
                condition.dateValueRange.before = temp;
            }
        }
        return {
            conditionType: RuleConditionType.DATE,
            value: condition.dateValueRange,
            transferDirection: condition.transferDirection,
        };
    }
}

function buildEntityConditionFromFormRow(
    condition: TransactionRuleConditionFormSchema,
): Omit<TransactionRuleCondition, "id"> | undefined {
    if (condition.entityValue) {
        return {
            conditionType: RuleConditionType.ENTITY,
            value: condition.entityValue,
            transferDirection: condition.transferDirection,
        };
    }
}

export function mapSplitConfigToSingleAmount(splitAction?: SplitActionFE) {
    return {
        splits: splitAction?.splits.map((split) => ({
            categoryId: split.categoryId,
            entityId: split.entityId,
            splitDescription: optionalSplitDescription(split.splitDescription),
            // Coerce null → undefined so SplitConfigSchema (z.number().optional())
            // accepts the Net row whose amount is null when not yet computed.
            amount: splitAction?.isPercentage
                ? (split.percentageAmount ?? undefined)
                : (split.amount ?? undefined),
            classIds: split.classIds,
            counterpartyId: split.counterpartyId,
            accountId: split.accountId,
            accrualAccountId: split.accrualAccountId,
            scheduleTemplateId: split.scheduleTemplateId,
        })),
        isPercentage: splitAction?.isPercentage ?? false,
        autoConfirm: splitAction?.autoConfirm ?? false,
    };
}

function buildCounterpartyConditionFromFormRow(
    condition: TransactionRuleConditionFormSchema,
): Omit<TransactionRuleCondition, "id"> | undefined {
    if (condition.counterpartyIds?.length) {
        return {
            conditionType: RuleConditionType.COUNTERPARTY,
            value: condition.counterpartyIds,
            transferDirection: condition.transferDirection,
        };
    }
}

function buildDateProximityConditionFromFormRow(
    condition: TransactionRuleConditionFormSchema,
): Omit<TransactionRuleCondition, "id"> | undefined {
    if (condition.dateProximity) {
        return {
            conditionType: RuleConditionType.DATE_PROXIMITY,
            value: condition.dateProximity,
            transferDirection: condition.transferDirection ?? null,
        };
    }
}

function buildSimilarToConditionFromFormRow(
    condition: TransactionRuleConditionFormSchema,
): Omit<TransactionRuleCondition, "id"> | undefined {
    if (condition.normalizedDescriptionId) {
        return {
            conditionType: RuleConditionType.SIMILAR_TO,
            // D2: emit the split-keyed object when the row carries a key; a bare string
            // (undefined key) keeps manually-created / legacy rules as whole-cluster.
            value:
                condition.effectiveSplitKey === undefined
                    ? condition.normalizedDescriptionId
                    : {
                          normalizedDescriptionId:
                              condition.normalizedDescriptionId,
                          effectiveSplitKey: condition.effectiveSplitKey,
                      },
            transferDirection: condition.transferDirection,
        };
    }
}

const AMOUNT_ROUNDING_FORM_TO_MODEL: Record<
    AmountRoundingConditionType,
    AmountRoundingLevel
> = {
    [AmountRoundingConditionType.DOLLAR]: AmountRoundingLevel.DOLLAR,
    [AmountRoundingConditionType.TEN]: AmountRoundingLevel.TEN,
    [AmountRoundingConditionType.HUNDRED]: AmountRoundingLevel.HUNDRED,
    [AmountRoundingConditionType.THOUSAND]: AmountRoundingLevel.THOUSAND,
};

interface ActionModelRow {
    value: string | number | string[];
    actionType: RuleActionType;
    order: number;
    transferDirection: TransferRuleDirection | null;
}

export function formActionsToModel(
    actions: TransactionRuleActionFormSchema[],
): ActionModelRow[] {
    const actionResults = actions
        .filter(
            (a): a is Required<TransactionRuleActionFormSchema> =>
                !!a.actionType && !!a.value,
        )
        .map((action, index) => {
            const row = ((): ActionModelRow | undefined => {
                switch (action.actionType) {
                    case FormRuleActionType.CATEGORY:
                        if (action.value.categoryId) {
                            return {
                                value: action.value.categoryId,
                                actionType: RuleActionType.CATEGORY,
                                order: index,
                                transferDirection: action.transferDirection,
                            };
                        }
                        return undefined;
                    case FormRuleActionType.ENTITY:
                        if (action.value.entityId) {
                            return {
                                value: action.value.entityId,
                                actionType: RuleActionType.ENTITY,
                                order: index,
                                transferDirection: action.transferDirection,
                            };
                        }
                        return undefined;
                    case FormRuleActionType.ACCOUNT_ID:
                    case FormRuleActionType.ACCOUNT_NAME:
                        // A multi-business covering set writes an array. The
                        // array is sent as-is even when it holds one uuid: the
                        // backend scalarizes it when the entity condition names
                        // one business, and rejects a scalar when it names
                        // several. This runs even with the flag off so a stored
                        // array round-trips.
                        if (action.value.accountIds?.length) {
                            return {
                                value: action.value.accountIds,
                                actionType: RuleActionType.ACCOUNT_ID,
                                order: index,
                                transferDirection: action.transferDirection,
                            };
                        }
                        if (action.value.accountId) {
                            return {
                                value: action.value.accountId,
                                actionType: RuleActionType.ACCOUNT_ID,
                                order: index,
                                transferDirection: action.transferDirection,
                            };
                        }
                        if (action.value.accountName) {
                            return {
                                value: action.value.accountName,
                                actionType: RuleActionType.ACCOUNT_NAME,
                                order: index,
                                transferDirection: action.transferDirection,
                            };
                        }
                        return undefined;
                    case FormRuleActionType.ACCRUAL_ACCOUNT_ID: {
                        const autoConfirm = !!action.value.autoConfirm;
                        if (action.value.accountIds?.length) {
                            return {
                                value: serializeAccrualAccountActionValue({
                                    accountIds: action.value.accountIds,
                                    autoConfirm,
                                }),
                                actionType: RuleActionType.ACCRUAL_ACCOUNT_ID,
                                order: index,
                                transferDirection: action.transferDirection,
                            };
                        }
                        if (action.value.accountId) {
                            return {
                                value: serializeAccrualAccountActionValue({
                                    accountId: action.value.accountId,
                                    autoConfirm,
                                }),
                                actionType: RuleActionType.ACCRUAL_ACCOUNT_ID,
                                order: index,
                                transferDirection: action.transferDirection,
                            };
                        }
                        if (action.value.accountName) {
                            return {
                                value: serializeAccrualAccountActionValue({
                                    accountName: action.value.accountName,
                                    autoConfirm,
                                }),
                                actionType: RuleActionType.ACCRUAL_ACCOUNT_NAME,
                                order: index,
                                transferDirection: action.transferDirection,
                            };
                        }
                        return undefined;
                    }
                    case FormRuleActionType.CLASS:
                        if (action.value?.classIds) {
                            return {
                                value: action.value?.classIds,
                                actionType: RuleActionType.CLASS,
                                order: index,
                                transferDirection: action.transferDirection,
                            };
                        }
                        return undefined;
                    case FormRuleActionType.MEMO:
                        if (action.value?.memo) {
                            return {
                                value: action.value.memo,
                                actionType: RuleActionType.MEMO,
                                order: index,
                                transferDirection: action.transferDirection,
                            };
                        }
                        return undefined;
                    case FormRuleActionType.COUNTERPARTY:
                        if (action.value?.counterpartyId) {
                            return {
                                value: action.value.counterpartyId,
                                actionType: RuleActionType.COUNTERPARTY,
                                order: index,
                                transferDirection: action.transferDirection,
                            };
                        }
                        return undefined;
                    case FormRuleActionType.CUSTOMER:
                        if (action.value?.customerId) {
                            return {
                                value: action.value.customerId,
                                actionType: RuleActionType.CUSTOMER,
                                order: index,
                                transferDirection: action.transferDirection,
                            };
                        }
                        return undefined;
                    case FormRuleActionType.ASSIGN_TO_SCHEDULE:
                        if (action.value.scheduleTemplateId) {
                            return {
                                value: serializeAssignToScheduleActionValue({
                                    scheduleTemplateId:
                                        action.value.scheduleTemplateId,
                                    autoPostOnApply:
                                        !!action.value.autoPostOnApply ||
                                        !!action.value.autoPostRecognition,
                                    autoPostRecognition:
                                        !!action.value.autoPostOnApply ||
                                        !!action.value.autoPostRecognition,
                                }),
                                actionType: RuleActionType.ASSIGN_TO_SCHEDULE,
                                order: index,
                                transferDirection: action.transferDirection,
                            };
                        }
                        return undefined;
                    case FormRuleActionType.SPLIT:
                        return {
                            value: JSON.stringify(
                                mapSplitConfigToSingleAmount(
                                    action.value?.splitAction,
                                ),
                            ),
                            actionType: RuleActionType.SPLIT,
                            order: index,
                            transferDirection: action.transferDirection,
                        };
                    default:
                        return undefined;
                }
            })();
            return row;
        })
        .filter(
            (action): action is ActionModelRow =>
                !!action?.value &&
                (!Array.isArray(action.value) || action.value.length > 0),
        );

    return actionResults;
}
